<template>
  <div id="recebimento-modal-app">
    <modal
      elem-id="modal_crud_recebimento"
      size="compactDesktop"
      :close-label="t('Fechar')"
      :save-label="t('Salvar recebimento')"
      :save-disabled-fn="() => !isFormValid || isFromProvider"
      :hide-save="isFromProvider"
      :save-fn="handleSave"
      :title="t('Recebimento')"
      :show="isVisible"
      @close="closeModal"
      @dismiss="closeModal"
      >

      <!-- Content only renders when modal is visible (prevents API calls on mount) -->
      <template v-if="isVisible">
        <!-- Pending Refund Alert -->
        <div v-if="hasPendingRefundRequest && pendingRefundInfo && pendingRefundInfo.amount" class="pending-refund-alert">
          <i class="pi pi-clock"></i>
          <div class="pending-refund-content">
            <strong>{{ t('Estorno pendente') }}</strong>
            <span>{{ pendingRefundInfo.amount }} {{ t('solicitado') }} {{ pendingRefundInfo.timeAgo }}</span>
            <small>{{ t('Aguardando confirmação do provedor de pagamento') }}</small>
          </div>
        </div>

        <!-- Action Buttons -->
        <div class="action-buttons">
          <!-- Refund Button - Only shown for recebimentos with provider charge ID and no pending refund -->
          <Button
            v-if="canRefund && !hasPendingRefundRequest"
            data-testid="recebimento-refund-button"
            :label="t('Estornar pagamento')"
            icon="pi pi-replay"
            severity="warn"
            size="small"
            @click="handleRefund"
            id="btnEstornarRecebimento" />

          <!-- Disabled Refund Button - Show when pending refund exists -->
          <Button
            v-if="canRefund && hasPendingRefundRequest"
            data-testid="recebimento-refund-pending-button"
            :label="t('Estorno pendente...')"
            icon="pi pi-spin pi-spinner"
            severity="secondary"
            size="small"
            :disabled="true"
            id="btnEstornarRecebimentoPending" />

          <!-- Delete Button -->
          <Button
            v-if="isEditMode && (recebimentoData.recebimento_id ?? 0) > 0"
            data-testid="recebimento-delete-button"
            :label="t('Excluir recebimento')"
            icon="pi pi-trash"
            severity="danger"
            size="small"
            @click="handleDelete"
            id="btnExcluirRecebimento" />
        </div>

        <!-- AI Completion (v1 users only) -->
        <object-completion
          v-if="usuarioVersao === 1"
          elem-id="recebimentosCompletionComponent"
          :label="t('Completar com IA')"
          v-model="completionData"
          :get-json-schema-to-fill="getJsonSchemaToFill"
          :extra-informations-object="getExtraInformations"
          :success-callback="handleInferSuccess"
          :error-callback="handleInferError"
          api-endpoint="/index.php/completions/completions/infer"
          :placeholder="t('Digite o conteúdo para completar')"
          :description="t('Use a IA para completar o objeto de recebimento. Digite, cole conversas ou imagens relevantes para a IA ajudar a completar o formulário.')"
          debug="false"
          :cancel-label="t('Cancelar')"
          :send-label="t('Enviar')"
          :ng-required="true">
        </object-completion>

        <!-- Recebimento Form -->
        <recebimento-form
          ref="formRef"
          v-model="recebimentoData"
          :is-edit-mode="isEditMode"
          :read-only="isFromProvider"
          :valor-restante-cap="valorRestanteCap"
          :atendente-or-operador="isAtendenteOrOperador">
        </recebimento-form>

        <!--
          POR QUE NÃO DÁ PARA SALVAR — o botão fica no rodapé do modal, longe do
          campo culpado, e desabilitado ele não explica nada (chamado 2026-0033).
        -->
        <div
          v-if="!isFromProvider && pendencias.length > 0"
          class="pendencias-salvar"
          data-testid="recebimento-pendencias">
          <i class="pi pi-exclamation-circle"></i>
          <div>
            <strong>{{ t('Para salvar, ainda falta') }}:</strong>
            <ul>
              <li v-for="motivo in pendencias" :key="motivo">{{ motivo }}</li>
            </ul>
          </div>
        </div>
      </template>
    </modal>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { eventBus } from '../../eventBus';
import { log } from '../../utils/DebugLogger';
import { autoriaDoUsuarioLogado } from '../../utils/recebimentoAutoria';
import { config } from '../../services/configService';
// Feature `baas` (backend registry, skill feature-flags).
const baasEnabled = config.isFeatureEnabled('baas');

import { useRecebimento } from '../../composables/useRecebimento';
import { mensagemDaRecusaDeParcelamento } from '../../composables/useParcelamento';
import { useConfirmAction } from '../../composables/useConfirmAction';
import { permissionsService } from '../../services/permissionsService';
import { useUserPermissions } from '../../composables/usePermissions';
import RecebimentoForm from './RecebimentoForm.vue';
import type { SubaccountsLinks_PendingRefundRequestRow } from '../../contracts/SubaccountsLinks_PendingRefundRequestRow';

type Id = number | string;

interface RecebimentoData {
  recebimento_id?: number;
  recebimento_data_cadastro?: string;
  recebimento_data?: string;
  recebimento_franquia_id?: number;
  recebimento_franquia_nome?: string;
  recebimento_conta_id?: number | string;
  recebimento_conta_nome?: string;
  recebimento_forma_pagamento_id?: number;
  recebimento_forma_pagamento_nome?: string;
  recebimento_origem?: string;
  recebimento_origem_id?: Id;
  recebimento_registrado_por?: string;
  recebimento_registrado_por_id?: Id;
  recebimento_valor?: number;
  recebimento_valor_taxas?: number;
  recebimento_valor_liquido?: number;
  recebimento_provedor?: string;
  recebimento_id_externo?: string;
  recebimento_observacoes?: string;
  // Venda parcelada (chamado 2026-0067)
  recebimento_numero_parcelas?: number;
  recebimento_parcela_numero?: number;
  recebimento_parcela_de?: number | null;
  [key: string]: unknown;
}

type PendingRefundRequest = SubaccountsLinks_PendingRefundRequestRow;

interface PendingRefundStatus {
  has_pending?: boolean;
  hasPending?: boolean;
  pending_request?: PendingRefundRequest | null;
}

interface PendingRefundInfo {
  amount?: string;
  timeAgo?: string;
  [key: string]: unknown;
}

interface FormRefShape {
  isFormValid?: boolean;
  /** O que falta para salvar, já em texto — ver RecebimentoForm. */
  pendencias?: string[];
  contasCarregadas?: Record<string, unknown>;
}

interface InferenceResult {
  recebimento_conta_id?: Id;
  recebimento_forma_pagamento_id?: Id;
  recebimento_valor_em_centavos?: number;
  recebimento_observacoes?: string;
}

interface PrefilledData {
  compromissoId?: number;
  franquiaId?: number;
  valorSugerido?: number;
  valorRestante?: number;
  origem?: string;
  origemId?: number | string;
}

interface ModalOpenPayload {
  id?: number;
  prefilledData?: PrefilledData;
  franquiaId?: number;
  compromissoId?: number;
}

interface RefundRequestedPayload {
  recebimento_id?: number;
}

withDefaults(defineProps<{
  usuarioVersao?: number | string;
}>(), {
  usuarioVersao: 0,
});

// Get i18n composable
const { t } = useI18n();

const { confirmAction } = useConfirmAction();

// Get recebimento composable
const {
  fetchRecebimento,
  createRecebimento,
  updateRecebimento,
  deleteRecebimento,
  canRefundRecebimento,
  getPendingRefundStatus,
  formatPendingRefund,
  isLoading
} = useRecebimento();

/**
 * Check and load pending refund status for a recebimento
 */
const checkPendingRefundStatus = async (recebimentoId: number | undefined): Promise<void> => {
  if (!baasEnabled) return; // refunds go through the provider charge — BaaS only
  // Reset to safe defaults first
  hasPendingRefundRequest.value = false;
  pendingRefundInfo.value = null;

  if (!recebimentoId) {
    return;
  }

  try {
    const pendingStatus = await getPendingRefundStatus(recebimentoId) as PendingRefundStatus;
    log('RecebimentoModal', 'checkPendingRefundStatus', 'Pending status result', pendingStatus);

    // Check for explicit has_pending flag
    const hasPending = pendingStatus.has_pending === true || pendingStatus.hasPending === true;

    // Also verify pending_request is a valid object with required fields
    const pendingRequest = pendingStatus.pending_request;
    const isValidPendingRequest = !!pendingRequest &&
      typeof pendingRequest === 'object' &&
      !!pendingRequest.id &&
      (pendingRequest.requested_amount ?? 0) > 0;

    if (hasPending && isValidPendingRequest && pendingRequest) {
      hasPendingRefundRequest.value = true;
      pendingRefundInfo.value = formatPendingRefund(pendingRequest) as PendingRefundInfo;
      log('RecebimentoModal', 'checkPendingRefundStatus', 'Formatted pending info', pendingRefundInfo.value);
    } else {
      log('RecebimentoModal', 'checkPendingRefundStatus', 'No valid pending request', {
        hasPending,
        isValidPendingRequest,
        pendingRequest
      });
    }
  } catch (error) {
    log('RecebimentoModal', 'checkPendingRefundStatus', 'Error checking pending status', error);
    // Already reset to safe defaults at start
  }
};

// Reactive data
const isVisible = ref<boolean>(false);
const isEditMode = ref<boolean>(false);
const formRef = ref<FormRefShape | null>(null);
const completionData = ref<string>('');
const hasPendingRefundRequest = ref<boolean>(false);
const pendingRefundInfo = ref<PendingRefundInfo | null>(null);
// Cap (in reais) passed down from prefilledData.valorRestante — only enforced
// for atendente/operador. Null means no cap (manager-or-above, or no prefill).
const valorRestanteCap = ref<number | null>(null);
const { isAtendenteOrOperador: isAtendenteOrOperadorComputed } = useUserPermissions();
const isAtendenteOrOperador = isAtendenteOrOperadorComputed;

// Default recebimento data
const getDefaultRecebimentoData = (): RecebimentoData => {
  const date = new Date();
  const offsetMinutes = date.getTimezoneOffset();
  const adjustedDate = new Date(date.getTime() - (offsetMinutes * 60000));

  const user = permissionsService.getUser();

  return {
    recebimento_id: 0,
    recebimento_data_cadastro: adjustedDate.toISOString().slice(0, 19).replace('T', ' '),
    recebimento_data: adjustedDate.toISOString().slice(0, 10), // Y-m-d format
    recebimento_franquia_id: 0,
    recebimento_franquia_nome: '',
    recebimento_conta_id: 0,
    recebimento_conta_nome: '',
    recebimento_forma_pagamento_id: 0,
    recebimento_forma_pagamento_nome: '',
    recebimento_origem: 'compromisso',
    recebimento_origem_id: 0,
    ...autoriaDoUsuarioLogado(),
    recebimento_valor: 0, // Gross value in centavos (what customer paid)
    recebimento_valor_taxas: 0, // Fee value in centavos (payment processor fees)
    recebimento_valor_liquido: 0, // Net value in centavos (valor - valor_taxas)
    recebimento_provedor: 'manual',
    recebimento_id_externo: '',
    recebimento_observacoes: '',
    recebimento_numero_parcelas: 1
  };
};

const recebimentoData = ref<RecebimentoData>(getDefaultRecebimentoData());

// Computed
/** O que o formulário diz que falta — mostrado junto do botão desabilitado. */
const pendencias = computed<string[]>(() => formRef.value?.pendencias ?? []);

const isFormValid = computed<boolean>(() => {
  return formRef.value?.isFormValid ?? false;
});

/**
 * Check if user has permission to refund payments
 * Only Full, Franqueado, and Gerente can perform refunds
 */
const hasRefundPermission = computed<boolean>(() => {
  return permissionsService.isUserInGroups(['Full', 'Franqueado', 'Gerente']);
});

/**
 * Check if this recebimento can be refunded via payment provider
 * Only available in edit mode for recebimentos with a provider charge ID
 * AND user must have refund permission (Full, Franqueado, or Gerente)
 */
const canRefund = computed<boolean>(() => {
  return baasEnabled && isEditMode.value &&
         (recebimentoData.value.recebimento_id ?? 0) > 0 &&
         canRefundRecebimento(recebimentoData.value) &&
         hasRefundPermission.value;
});

/**
 * Check if this recebimento came from a payment provider
 * Provider recebimentos should be read-only (cannot be manually edited)
 */
const isFromProvider = computed<boolean>(() => {
  return isEditMode.value && canRefundRecebimento(recebimentoData.value);
});

// Methods
const openModal = (): void => {
  log('RecebimentoModal', 'openModal', 'Opening modal');
  isVisible.value = true;
};

const closeModal = (): void => {
  log('RecebimentoModal', 'closeModal', 'Closing modal');
  isVisible.value = false;
  isEditMode.value = false;
  recebimentoData.value = getDefaultRecebimentoData();
  // Reset pending refund state
  hasPendingRefundRequest.value = false;
  pendingRefundInfo.value = null;
  // Reset valor cap so a subsequent edit-mode open doesn't inherit a stale cap
  valorRestanteCap.value = null;
};

const loadForNew = async (franquiaId: number, compromissoId: number): Promise<void> => {
  log('RecebimentoModal', 'loadForNew', 'Initializing new recebimento', { franquiaId, compromissoId });

  isEditMode.value = false;
  recebimentoData.value = getDefaultRecebimentoData();
  recebimentoData.value.recebimento_franquia_id = franquiaId;
  recebimentoData.value.recebimento_origem = 'compromisso';
  recebimentoData.value.recebimento_origem_id = String(compromissoId);

  openModal();
};

const loadForNewWithPrefilledData = async (prefilledData: PrefilledData): Promise<void> => {
  log('RecebimentoModal', 'loadForNewWithPrefilledData', 'Initializing new recebimento with prefilled data', prefilledData);

  isEditMode.value = false;
  recebimentoData.value = getDefaultRecebimentoData();

  // Apply prefilled data
  if (prefilledData.origem) {
    recebimentoData.value.recebimento_origem = prefilledData.origem;
    if (prefilledData.origemId) {
      recebimentoData.value.recebimento_origem_id = String(prefilledData.origemId);
    }
  } else if (prefilledData.compromissoId) {
    recebimentoData.value.recebimento_origem = 'compromisso';
    recebimentoData.value.recebimento_origem_id = String(prefilledData.compromissoId);
  }

  if (prefilledData.franquiaId) {
    recebimentoData.value.recebimento_franquia_id = prefilledData.franquiaId;
  }

  // Pre-fill suggested payment amount (in centavos)
  if (prefilledData.valorSugerido) {
    const valor = Math.round(prefilledData.valorSugerido * 100);
    recebimentoData.value.recebimento_valor = valor;
    recebimentoData.value.recebimento_valor_taxas = 0;
    recebimentoData.value.recebimento_valor_liquido = valor;
  } else if (prefilledData.valorRestante) {
    const valor = Math.round(prefilledData.valorRestante * 100);
    recebimentoData.value.recebimento_valor = valor;
    recebimentoData.value.recebimento_valor_taxas = 0;
    recebimentoData.value.recebimento_valor_liquido = valor;
  }

  // Stash the cap (in reais) so RecebimentoForm can enforce it for atendente/operador.
  // We always set this when valorRestante is supplied — even for manager-or-above —
  // because the form's `atendente-or-operador` prop is what actually gates enforcement.
  valorRestanteCap.value = typeof prefilledData.valorRestante === 'number'
    ? prefilledData.valorRestante
    : null;

  log('RecebimentoModal', 'loadForNewWithPrefilledData', 'Pre-filled valor', recebimentoData.value.recebimento_valor);

  openModal();
};

const loadForEdit = async (recebimentoId: number): Promise<void> => {
  log('RecebimentoModal', 'loadForEdit', 'Loading recebimento for edit', recebimentoId);

  try {
    const data = await fetchRecebimento(recebimentoId) as RecebimentoData;

    // Parcela de uma venda parcelada MANUAL: quem edita é o modal da venda inteira (chamado
    // 2026-0067) — abrir aqui mostraria uma parcela de R$ 9,75 de uma venda de R$ 39,00. As do
    // provedor ficam aqui, somente leitura, com o estorno por cobrança.
    const provedor = String(data.recebimento_provedor ?? '');
    if (Number(data.recebimento_numero_parcelas ?? 1) > 1 && (provedor === '' || provedor === 'manual')) {
      eventBus.emit('modal.recebimento-parcelado.open', { id: recebimentoId });
      return;
    }

    isEditMode.value = true;
    recebimentoData.value = { ...data };

    // Check for pending refund status (only for recebimentos that can be refunded)
    if (canRefundRecebimento(data)) {
      await checkPendingRefundStatus(recebimentoId);
    } else {
      hasPendingRefundRequest.value = false;
      pendingRefundInfo.value = null;
    }

    openModal();
  } catch (error) {
    log('RecebimentoModal', 'loadForEdit', 'Error loading recebimento', error);
    eventBus.emit('globalMessage.error', t('Erro ao carregar recebimento'));
  }
};

const handleSave = async (): Promise<void> => {
  log('RecebimentoModal', 'handleSave', 'Saving recebimento', recebimentoData.value);

  if (!isFormValid.value) {
    log('RecebimentoModal', 'handleSave', 'Form is invalid');
    return;
  }

  try {
    const d = recebimentoData.value;
    const payload = {
      recebimento_id: d.recebimento_id,
      recebimento_data: d.recebimento_data,
      recebimento_franquia_id: d.recebimento_franquia_id,
      recebimento_forma_pagamento_id: d.recebimento_forma_pagamento_id,
      recebimento_valor: d.recebimento_valor,
      recebimento_valor_taxas: d.recebimento_valor_taxas,
      recebimento_valor_liquido: d.recebimento_valor_liquido,
      recebimento_provedor: d.recebimento_provedor,
      recebimento_origem: d.recebimento_origem,
      recebimento_origem_id: d.recebimento_origem_id !== undefined ? Number(d.recebimento_origem_id) : undefined,
      recebimento_registrado_por: d.recebimento_registrado_por,
      recebimento_registrado_por_id: d.recebimento_registrado_por_id !== undefined ? String(d.recebimento_registrado_por_id) : undefined,
      recebimento_id_externo: d.recebimento_id_externo,
      recebimento_conta_id: d.recebimento_conta_id !== undefined ? Number(d.recebimento_conta_id) : undefined,
      recebimento_observacoes: d.recebimento_observacoes,
      // Parcelar é só na criação; o backend gera um recebimento por parcela (chamado 2026-0067).
      recebimento_numero_parcelas: !isEditMode.value && (d.recebimento_numero_parcelas ?? 1) > 1
        ? d.recebimento_numero_parcelas
        : undefined,
    };
    if (isEditMode.value && (d.recebimento_id ?? 0) > 0) {
      await updateRecebimento(payload);
      log('RecebimentoModal', 'handleSave', 'Recebimento updated successfully');
    } else {
      await createRecebimento(payload);
      log('RecebimentoModal', 'handleSave', 'Recebimento created successfully');
    }

    // Emit refresh event for the specific compromisso
    if (recebimentoData.value.recebimento_origem_id) {
      eventBus.emit(`refresh-recebimento-${recebimentoData.value.recebimento_origem_id}`);
    }

    // Emit general refresh event
    eventBus.emit('recebimento.refresh');

    // Also refresh compromisso to update saldo, only when this recebimento
    // actually belongs to a compromisso
    if (recebimentoData.value.recebimento_origem === 'compromisso') {
      eventBus.emit('compromisso.refresh');
    }

    // Refresh PDV data when recebimento is for a service order
    if (recebimentoData.value.recebimento_origem === 'so_order') {
      eventBus.emit('pdv.saved');
    }

    closeModal();
  } catch (error) {
    log('RecebimentoModal', 'handleSave', 'Error saving recebimento', error);
    eventBus.emit('globalMessage.error', mensagemDaRecusaDeParcelamento(error, t) ?? t('Erro ao salvar recebimento'));
  }
};

const handleDelete = (): void => {
  log('RecebimentoModal', 'handleDelete', 'Deleting recebimento', recebimentoData.value.recebimento_id);

  confirmAction({
    message: t('Tem certeza que deseja excluir este recebimento?'),
    header: t('Confirmação'),
    onAccept: async () => {
      try {
        await deleteRecebimento(recebimentoData.value.recebimento_id ?? 0);
        log('RecebimentoModal', 'handleDelete', 'Recebimento deleted successfully');

        // Emit refresh event for the specific compromisso
        if (recebimentoData.value.recebimento_origem_id) {
          eventBus.emit(`refresh-recebimento-${recebimentoData.value.recebimento_origem_id}`);
        }

        // Emit general refresh event
        eventBus.emit('recebimento.refresh');

        // Also refresh compromisso to update saldo, only when this recebimento
        // actually belongs to a compromisso
        if (recebimentoData.value.recebimento_origem === 'compromisso') {
          eventBus.emit('compromisso.refresh');
        }

        // Refresh PDV data when recebimento is for a service order
        if (recebimentoData.value.recebimento_origem === 'so_order') {
          eventBus.emit('pdv.saved');
        }

        closeModal();
      } catch (error) {
        log('RecebimentoModal', 'handleDelete', 'Error deleting recebimento', error);
        // O servidor diz por que recusou (permissão, provedor de pagamento…); só na falta disso a genérica.
        eventBus.emit('globalMessage.error', (error instanceof Error && error.message) ? t(error.message) : t('Erro ao excluir recebimento'));
      }
    }
  });
};

/**
 * Handle refund of this recebimento via payment provider
 * Opens the RefundWizard to allow user to set amount and confirm
 */
const handleRefund = (): void => {
  log('RecebimentoModal', 'handleRefund', 'Opening refund wizard', {
    recebimento_id: recebimentoData.value.recebimento_id,
    recebimento_id_externo: recebimentoData.value.recebimento_id_externo
  });

  // Open refund wizard with recebimento data (keep this modal open)
  eventBus.emit('modal.refund-wizard.open', {
    recebimento: { ...recebimentoData.value }
  });
};

// AI Completion methods
const getJsonSchemaToFill = (): Record<string, unknown> => {
  return {
    recebimento_conta_id: recebimentoData.value.recebimento_conta_id,
    recebimento_forma_pagamento_id: recebimentoData.value.recebimento_forma_pagamento_id,
    recebimento_valor_em_centavos: recebimentoData.value.recebimento_valor,
    recebimento_observacoes: recebimentoData.value.recebimento_observacoes
  };
};

const getExtraInformations = (): Record<string, unknown> => {
  // Get loaded contas from form
  const contas = formRef.value?.contasCarregadas || {};
  return {
    contas: contas
  };
};

const handleInferSuccess = (result: InferenceResult): void => {
  log('RecebimentoModal', 'handleInferSuccess', 'Inference successful', result);

  setTimeout(() => {
    if (result.recebimento_conta_id) {
      recebimentoData.value.recebimento_conta_id = String(result.recebimento_conta_id);
    }
    if (result.recebimento_forma_pagamento_id) {
      recebimentoData.value.recebimento_forma_pagamento_id = Number(result.recebimento_forma_pagamento_id);
    }
    if (result.recebimento_valor_em_centavos) {
      recebimentoData.value.recebimento_valor = result.recebimento_valor_em_centavos;
    }
    if (result.recebimento_observacoes) {
      recebimentoData.value.recebimento_observacoes = result.recebimento_observacoes;
    }
  }, 0);
};

const handleInferError = (error: unknown): void => {
  log('RecebimentoModal', 'handleInferError', 'Inference error', error);
  eventBus.emit('globalMessage.error', t('Erro ao inferir dados do recebimento') + ': ' + String(error));
};

// Standard modal open handler (for modal.recebimento.open event)
const handleModalOpen = (data?: ModalOpenPayload): void => {
  log('RecebimentoModal', 'handleModalOpen', 'Standard modal open event received', data);

  if (data?.id) {
    // Edit mode
    loadForEdit(data.id);
  } else if (data?.prefilledData) {
    // New with prefilled data
    loadForNewWithPrefilledData(data.prefilledData);
  } else if (data?.franquiaId) {
    // New for specific franquia/compromisso
    loadForNew(data.franquiaId, data.compromissoId ?? 0);
  } else {
    // Simple open
    openModal();
  }
};

// Handler for when a refund is requested - refreshes pending status if modal is open
const handleRefundRequested = async (data?: RefundRequestedPayload): Promise<void> => {
  log('RecebimentoModal', 'handleRefundRequested', 'Refund requested event received', data);

  // Only refresh if the modal is visible and showing the same recebimento
  if (data && isVisible.value && recebimentoData.value.recebimento_id === data.recebimento_id) {
    log('RecebimentoModal', 'handleRefundRequested', 'Refreshing pending status for current recebimento');
    await checkPendingRefundStatus(data.recebimento_id);
  }
};

// Legacy event wrappers — eventBus has tightened types so promise-returning handlers need wrappers
const handleLegacyOpenModalNew = (franquiaId?: number): void => {
  loadForNew(franquiaId ?? 0, 0);
};
const handleLegacyOpenModalCreate = (prefilled?: PrefilledData): void => {
  if (prefilled) loadForNewWithPrefilledData(prefilled);
};
const handleLegacyOpenModalEdit = (recebimentoId?: number): void => {
  if (recebimentoId) loadForEdit(recebimentoId);
};

// Event listeners
onMounted(() => {
  log('RecebimentoModal', 'onMounted', 'Component mounted');

  // Standard modal events (preferred pattern)
  eventBus.on('modal.recebimento.open', handleModalOpen);
  eventBus.on('modal.recebimento.close', closeModal);

  // Refund event - refresh pending status when refund is requested
  eventBus.on('recebimento.refunded', handleRefundRequested);

  // Legacy events (for backward compatibility)
  eventBus.on('recebimento.open-modal-new', handleLegacyOpenModalNew);
  eventBus.on('recebimento.open-modal-create', handleLegacyOpenModalCreate);
  eventBus.on('recebimento.open-modal-edit', handleLegacyOpenModalEdit);
});

onBeforeUnmount(() => {
  log('RecebimentoModal', 'onBeforeUnmount', 'Component unmounting');

  // Remove standard modal events
  eventBus.off('modal.recebimento.open', handleModalOpen);
  eventBus.off('modal.recebimento.close', closeModal);

  // Remove refund event
  eventBus.off('recebimento.refunded', handleRefundRequested);

  // Remove legacy events
  eventBus.off('recebimento.open-modal-new', handleLegacyOpenModalNew);
  eventBus.off('recebimento.open-modal-create', handleLegacyOpenModalCreate);
  eventBus.off('recebimento.open-modal-edit', handleLegacyOpenModalEdit);
});

// Expose methods for parent components
defineExpose({
  loadForNew,
  loadForEdit,
  openModal,
  closeModal
});
</script>

<style scoped>
/* Action buttons container - replaces Bootstrap .text-right */
.action-buttons {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-bottom: 20px;
}

/* Pending refund alert styles */
.pending-refund-alert {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 16px;
  margin-bottom: 16px;
  background-color: #fff3cd;
  border: 1px solid #ffc107;
  border-radius: 6px;
  color: #856404;
}

.pending-refund-alert > i {
  font-size: 1.25rem;
  margin-top: 2px;
  color: #856404;
}

.pending-refund-content {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.pending-refund-content strong {
  font-size: 0.95rem;
  color: #664d03;
}

.pending-refund-content span {
  font-size: 0.875rem;
}

.pending-refund-content small {
  font-size: 0.75rem;
  opacity: 0.85;
}

.pendencias-salvar {
  display: flex;
  gap: 0.5rem;
  align-items: flex-start;
  margin-top: 0.75rem;
  padding: 0.75rem;
  border-radius: 6px;
  background: var(--app-warning-bg, #fff4e5);
  color: var(--app-warning-text, #7a4b00);
  font-size: 0.875rem;
}

.pendencias-salvar ul {
  margin: 0.25rem 0 0;
  padding-left: 1.1rem;
}
</style>
